import { useState, useEffect } from 'react';
import { ChatMessage } from '../types';
import { messagesRef, createMessage } from '../../../../api/messages';
import { useContext } from 'react';
import AppContext from '../../../AppContext';

const MESSAGES_TO_FETCH = 50;
const MESSAGES_TO_DISPLAY = 30;

export function useChat() {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { user, login } = useContext(AppContext);

  useEffect(() => {
    try {
      setLoading(true);
      const ref = messagesRef()
        .orderByChild('timestamp')
        .limitToLast(MESSAGES_TO_FETCH);

      const unsubscribe = ref.on(
        'value',
        (snapshot) => {
          const messages = snapshot.val();
          const chatMessages: ChatMessage[] = messages
            ? Object.entries(messages)
                .map(([id, msg]: [string, any]) => ({
                  id,
                  text: msg.content,
                  sender: msg.userId === user?.uid ? 'user' : 'other',
                  timestamp: msg.timestamp,
                  username: msg.username,
                }))
                .sort((a, b) => a.timestamp - b.timestamp)
                .slice(-MESSAGES_TO_DISPLAY)
            : [];

          setMessages(chatMessages);
          setLoading(false);
          setError(null);
        },
        (error) => {
          console.error('Error fetching messages:', error);
          setError('Failed to load messages');
          setLoading(false);
        }
      );

      return () => {
        ref.off('value', unsubscribe);
      };
    } catch (error) {
      console.error('Error setting up chat:', error);
      setError('Failed to initialize chat');
      setLoading(false);
    }
  }, [user]);

  const handleSubmit = async () => {
    if (!user?.uid) {
      login();
      return;
    }

    if (!input.trim()) return;

    try {
      await createMessage({
        content: input,
        uid: user.uid,
        username: user.displayName || 'Anonymous',
      });
      setInput('');
      setError(null);
    } catch (error) {
      console.error('Error with message:', error);
      setError('Failed to send message');
    }
  };

  return {
    messages,
    input,
    setInput,
    handleSubmit,
    error,
    loading,
    isAuthenticated: !!user,
  };
}
